import { forwardRef } from 'react';
import type { PropsWithoutRef } from 'react';

import type { Action, NoChildrenProp, RefElement, TestIdProp, WithAttributes } from '../../types';
import FormField from '../FormField';
import FormControl from '../FormControl';
import type { FormControlProps } from '../FormControl';
import { createClassName, hasProp, withTestIds } from '../../utils';
import { useTestIds, useUID } from '../../hooks';

import StyledInput from './Input.styles';
import { getInputTestIds } from './Input.test-ids';

export type InputProps = WithAttributes<
  'input',
  FormControlProps &
    TestIdProp &
    NoChildrenProp & {
      /**
       * Specifies the type of input to be used.
       * @default 'text'
       */
      type?:
        | 'text'
        | 'password'
        | 'number'
        | 'email'
        | 'url'
        | 'search'
        | 'tel'
        | 'date'
        | 'datetime-local'
        | 'time'
        | 'week'
        | 'month'
        | 'hidden'
        | 'color';
      /** Pass an array of Action objects to append button(s) inline with the Input. */
      actions?: Action[];
    }
>;

export default withTestIds(
  forwardRef<RefElement<InputProps>, PropsWithoutRef<InputProps>>(function Input(props, ref) {
    const uid = useUID();

    const {
      testId,
      id = uid,
      value,
      defaultValue,
      required = false,
      disabled = false,
      readOnly = false,
      label,
      additionalInfo,
      labelHidden,
      info,
      status,
      actions,
      onResolveSuggestion,
      className,
      ...restProps
    } = props;

    const testIds = useTestIds(testId, getInputTestIds);

    const controlProp: {
      value?: string;
      defaultValue?: string;
    } = {};

    // Conditionally render component as controlled/uncontrolled
    if (hasProp(props, 'value')) {
      controlProp.value = value ?? '';
    } else if (hasProp(props, 'defaultValue')) {
      controlProp.defaultValue = defaultValue ?? '';
    }

    const Comp = (
      <FormControl
        data-testid={testIds.control}
        ref={ref}
        id={id}
        required={required}
        disabled={disabled}
        readOnly={readOnly}
        status={status}
        hasSuggestion={!!onResolveSuggestion && status === 'pending'}
        {...controlProp}
        // Disables browser autocomplete
        // Chrome does not respect autocomplete='off'
        // Current consensus is to workaround via random string
        autoComplete='_off'
        {...restProps}
        as={StyledInput}
        className={createClassName('input', className)}
      />
    );

    return label ? (
      <FormField
        testId={testIds}
        additionalInfo={additionalInfo}
        label={label}
        labelHidden={labelHidden}
        id={id}
        info={info}
        readOnly={readOnly}
        status={status}
        required={required}
        disabled={disabled}
        actions={actions}
        onResolveSuggestion={onResolveSuggestion}
      >
        {Comp}
      </FormField>
    ) : (
      Comp
    );
  }),
  getInputTestIds
);
